/**
 * Heuristic for "these are probably the same person" across mentions
 * extracted independently from different pages/records — e.g. "K Hutchison"
 * and "Kim Hutchison", or a bare first name from a casual signature ("Hi
 * Kim,") against a full reference elsewhere ("K Hutchison"). Each mention
 * is extracted from its own page with no awareness of any other mention,
 * so the same person often comes back under a noticeably different form,
 * including missing the surname entirely.
 *
 * Deliberately conservative: if both sides have a surname, they must
 * agree — "Rachel Poulton" and "Nigel Poulton" never merge just because
 * they share one. A bare first name only merges into a fuller name that
 * has a surname; two bare first names never merge with each other (no way
 * to tell if they're the same person). Exact matches (case-insensitive)
 * are the caller's job, not this heuristic's.
 *
 * "Agree" on the surname allows a one-edit spelling/OCR variant of a
 * longer surname ("Hosseini" vs "Hossein") via surnamesLikelyMatch below —
 * restricted to longer surnames specifically so a single-character
 * difference stays a small proportion of the name, not a coin flip
 * between two genuinely different short surnames ("Hall" vs "Ball").
 */
function levenshteinDistance(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i++) {
    const curr = [i];
    for (let j = 1; j <= n; j++) {
      curr[j] = a[i - 1] === b[j - 1]
        ? prev[j - 1]
        : 1 + Math.min(prev[j - 1], prev[j], curr[j - 1]);
    }
    prev = curr;
  }
  return prev[n];
}

function surnamesLikelyMatch(a, b) {
  if (a === b) return true;
  if (Math.min(a.length, b.length) < 6) return false;
  return levenshteinDistance(a, b) <= 1;
}

export function namesLikelyMatch(a, b) {
  if (!a || !b) return false;
  const normA = a.trim().toLowerCase();
  const normB = b.trim().toLowerCase();
  if (normA === normB) return false;

  const ta = normA.split(/\s+/);
  const tb = normB.split(/\s+/);
  const firstA = ta[0].replace(/\.$/, '');
  const firstB = tb[0].replace(/\.$/, '');
  const surnameA = ta.length > 1 ? ta[ta.length - 1] : null;
  const surnameB = tb.length > 1 ? tb[tb.length - 1] : null;

  // Both have a surname and they disagree (and aren't a spelling variant
  // of each other) — different people.
  if (surnameA && surnameB && !surnamesLikelyMatch(surnameA, surnameB)) return false;
  // Neither has a surname — no way to tell these apart, don't merge.
  if (!surnameA && !surnameB) return false;

  // First-name sides must agree, or one be an initial of the other.
  const firstNamesCompatible =
    firstA === firstB
    || (firstA.length === 1 && firstB.startsWith(firstA))
    || (firstB.length === 1 && firstA.startsWith(firstB));
  return firstNamesCompatible;
}

/**
 * Prefer whichever side has more information — a surname, then a fuller
 * first name, then (for a surname spelling variant, e.g. "Hosseini" vs
 * "Hossein") the longer surname spelling as more likely to be complete.
 */
export function fullerName(a, b) {
  const tokens = (s) => s.trim().split(/\s+/);
  const ta = tokens(a);
  const tb = tokens(b);
  if (ta.length !== tb.length) return ta.length > tb.length ? a : b;
  const firstLen = (t) => (t[0] || '').replace(/\.$/, '').length;
  if (firstLen(ta) !== firstLen(tb)) return firstLen(tb) > firstLen(ta) ? b : a;
  const lastLen = (t) => (t[t.length - 1] || '').length;
  return lastLen(tb) > lastLen(ta) ? b : a;
}

/**
 * Merge a list of { name, ...rest } entries, combining any two whose names
 * satisfy namesLikelyMatch. `combine(kept, incoming)` merges two matched
 * entries into one (decide how to merge whatever else the entries carry —
 * roles, record sets, etc.); this function overwrites the merged result's
 * `name` with the fuller of the two afterwards, so `combine` doesn't need
 * to worry about that part.
 */
export function mergeSimilarPeople(entries, combine) {
  const out = [];
  for (const entry of entries) {
    const idx = out.findIndex((o) => namesLikelyMatch(o.name, entry.name));
    if (idx === -1) {
      out.push(entry);
      continue;
    }
    const merged = combine(out[idx], entry);
    merged.name = fullerName(out[idx].name, entry.name);
    out[idx] = merged;
  }
  return out;
}
